// ─────────────────────────────────────────────────────────────────────────────
// REQ-102 — Muro de post-its. Feed REAL (useRecognitions), layout de moodboard, efecto typewriter,
// pin/color por Fundamental (resuelto contra el catálogo), contador "+N" clickeable, etiqueta
// grupal, tags de origen con logo, edición inline, reacciones (tras flag) y modal de detalle.
// Estados loading/error/empty reales. CERO mocks — cada dato viene de la API.
//
// FASE 4 (paridad demo, H-17..H-27): familia "rec" + "-postit-*" (namespace viejo) renombrada a `.postit-*` (mapeo 1:1
// con el demo, `global.css` : 761-1222) y `.rec-wall-*` a `.moodboard-*` (H-52). Se añaden: icono
// de Fundamental (`<img class="postit-fund-icon">`, H-18), etiquetas con logo (H-51, ver
// `PostItTagsArea` en `postitShared.tsx`), edición inline en la tarjeta (H-23) y reacciones
// maquetadas tras feature flag (H-19/H-50, ver `RECOGNITION_FEATURES.reactions`).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from 'react';
import { nombresDeReceptores, useReparadorDeNombres } from './nombresDeReconocimiento';
import type { MutableRefObject, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  useRecognitions,
  useRecognitionCatalog,
  useRecognitionRules,
  useUpdateRecognition,
  getFundamentalColor,
  getFundamentalName,
  getRecognitionErrorMessage,
  FUNDAMENTAL_PALETTE,
} from '@/api/recognition';
import type { RecognitionDto, FundamentalDto, RecognitionFeedParams } from '@/api/recognition';
import { useUserContext } from '@/contexts/UserContext';
import { StateBlock } from './components';
import { PostItDetailModal } from './PostItDetailModal';
import {
  PostItTagsArea,
  PostItReactionBadge,
  RecognitionFundIcon,
  resolveDefaultActionConfig,
  puedeEditarTarjeta,
  isEditedMessageValid,
  formatDate,
  POSTIT_PAPER_COLORS,
  getPostItPaperColor,
  canReportRecognition,
  isBrillamosOrigin,
  nombreDelEmisor,
  PuntosDeLaNominacion,
} from './postitShared';
import type { PostItActionConfig } from './postitShared';

const PAPER_ROTATIONS = [-3, 2, -1.5, 3, -2, 1.5];
const NAMES_SHOWN = 2;
/** Vista previa del mensaje en la tarjeta del muro (el mensaje completo, hasta 3000 caracteres,
 *  solo se ve en el modal de detalle — `PostItDetailModal.tsx`, sin truncar). */
const WALL_MESSAGE_PREVIEW_LIMIT = 60;

/** Recorta el mensaje a `WALL_MESSAGE_PREVIEW_LIMIT` caracteres para la tarjeta del muro. */
function truncateForWall(message: string): string {
  if (message.length <= WALL_MESSAGE_PREVIEW_LIMIT) return message;
  return `${message.slice(0, WALL_MESSAGE_PREVIEW_LIMIT)}...`;
}

interface PostItProps {
  recognition: RecognitionDto;
  index: number;
  color: string;
  darkColor: string;
  pin: string | null;
  /** Catálogo completo de Fundamentales — resuelve el ícono de la esquina (`RecognitionFundIcon`,
   *  hover con todos los Fundamentales cuando el reconocimiento tiene más de uno). */
  catalog: FundamentalDto[];
  isOwner: boolean;
  canEdit: boolean;
  /** Editar y eliminar viajan juntos en el servidor (`PostEditPolicy`): la papelera sigue la misma
   *  respuesta por tarjeta que el lápiz, para no ofrecer un borrado que devolverá 403. */
  canDelete: boolean;
  onOpen: () => void;
  profile?: boolean;
  minimal?: boolean;
  fundamentalName?: string;
  /** REQ-107 — mínimo de caracteres configurado por la OU para el mensaje (mismo criterio que el envío). */
  minCommentChars: number;
}

/**
 * H-drag — el post-it se vuelve `motion.div` (arrastrable) solo en modo perfil; en el resto de
 * muros (Reconocimiento, Brillamos) sigue siendo un `div` normal. `as React.ElementType` evita el
 * choque de tipos entre `motion.div` (forwardRef component) y el intrínseco `'div'`.
 *
 * `onDragStart`/`onDragEnd` marcan la bandera `justDraggedRef`: framer-motion NO cancela el
 * `click` nativo que el navegador dispara al soltar el arrastre, así que sin esa bandera cada
 * drop abriría además el modal de detalle. Se limpia en el siguiente tick, ya pasado ese click.
 */
function dragProps(justDraggedRef: MutableRefObject<boolean>) {
  return {
    drag: true,
    dragMomentum: false,
    dragElastic: 0.1,
    whileDrag: { scale: 1.05, zIndex: 100, boxShadow: '0 20px 40px rgba(0,0,0,0.18)' },
    whileHover: { scale: 1.02, zIndex: 50 },
    onDragStart: () => { justDraggedRef.current = true; },
    onDragEnd: () => { setTimeout(() => { justDraggedRef.current = false; }, 0); },
  };
}

/**
 * Click sobre la tarjeta: abre el detalle salvo que esté en edición inline o que el click sea el
 * que el navegador dispara al soltar un arrastre. La bandera se lee AQUÍ (no en el render) porque
 * `onDragEnd` la limpia en el tick siguiente, ya pasado ese click.
 */
function openDetailUnlessBusy(isEditing: boolean, justDraggedRef: MutableRefObject<boolean>, onOpen: () => void) {
  if (isEditing || justDraggedRef.current) return;
  onOpen();
}

interface PostItInlineEditorProps {
  draft: string;
  onDraftChange: (value: string) => void;
  draftValid: boolean;
  minCommentChars: number;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onCancel: () => void;
  onSave: () => void;
}

/**
 * Edición inline del mensaje (demo H-23): textarea + aviso de mínimo de caracteres + error + acciones.
 * Extraído de `PostIt` (que ya orquesta typewriter, drag y acciones) para mantener su complejidad
 * cognitiva dentro del límite del lint — no por reutilización, es la única llamada.
 */
function PostItInlineEditor({
  draft, onDraftChange, draftValid, minCommentChars, isPending, isError, error, onCancel, onSave,
}: Readonly<PostItInlineEditorProps>) {
  const { t } = useTranslation('recognition');
  return (
    <div className="postit-edit-wrap" onClick={(e) => e.stopPropagation()}>
      <textarea
        className="postit-edit-textarea"
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        rows={3}
        autoFocus
      />
      {!draftValid && (
        <p className="postit-edit-hint">
          {t('actions.editMinChars', { min: minCommentChars, count: draft.trim().length })}
        </p>
      )}
      {isError && (
        <p className="rp-error" role="alert">{getRecognitionErrorMessage(error, t('actions.editError'))}</p>
      )}
      <div className="postit-edit-actions">
        <button type="button" className="postit-edit-cancel" onClick={onCancel}>{t('common.cancel')}</button>
        <button type="button" className="postit-edit-save" disabled={isPending || !draftValid} onClick={onSave}>
          {isPending ? t('panel.sending') : t('actions.saveEdit')}
        </button>
      </div>
    </div>
  );
}

/**
 * Pin del post-it: círculo morado con ⭐ y resplandor CSS para reconocimientos de Brillamos Juntos
 * (`isBrillamosOrigin`), o el pin de color del Fundamental para el resto. `null` sin ninguno de
 * los dos (post-it sin Fundamental resuelto).
 */
function PostItPin({ recognition, fundamentalColor }: Readonly<{
  recognition: RecognitionDto;
  fundamentalColor: string | null;
}>) {
  if (isBrillamosOrigin(recognition)) {
    return <span className="postit-pin postit-pin--brillamos" aria-hidden="true">🌟</span>;
  }
  if (!fundamentalColor) return null;
  return <span className="postit-pin" style={{ '--pin-color': fundamentalColor } as React.CSSProperties} />;
}

/** Un post-it con el mensaje escribiéndose (typewriter) y autor revelado al terminar. */
function PostIt({ recognition, index, color, darkColor, pin, catalog, isOwner, canEdit, canDelete, onOpen, profile = false, minimal = false, fundamentalName, minCommentChars }: Readonly<PostItProps>) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language;
  const full = recognition.mensaje;
  /** Solo la tarjeta se recorta — el mensaje completo (`full`) se sigue usando para la edición
   *  inline y el detalle ampliado (`PostItDetailModal.tsx`) lo muestra sin truncar. */
  const preview = truncateForWall(full);
  const [typed, setTyped] = useState('');
  const [done, setDone] = useState(false);
  const idxRef = useRef(0);
  /** Se pone en true mientras dura un arrastre para que el `click` posterior no abra el detalle. */
  const justDraggedRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(full);
  const update = useUpdateRecognition();

  useEffect(() => {
    setTyped('');
    setDone(false);
    idxRef.current = 0;
    let timer: ReturnType<typeof setTimeout>;
    const start = setTimeout(() => {
      const tick = () => {
        if (idxRef.current < preview.length) {
          idxRef.current += 1;
          setTyped(preview.slice(0, idxRef.current));
          timer = setTimeout(tick, 26);
        } else {
          setDone(true);
        }
      };
      tick();
    }, index * 160);
    return () => { clearTimeout(start); clearTimeout(timer); };
  }, [preview, index]);

  // T4 — defensa propia del front: si el reconocimiento es anónimo (o el backend no mandó nombre) se
  // muestra "Anónimo" sin depender solo de que el servidor haya puesto emisorNombre=null.
  // VIS-6 — nunca un id: nombres rotos se reparan en vivo o dicen «Usuario no disponible». Antes,
  // sin `receptorNombres`, se pintaban los `receptorIds` tal cual.
  const reparar = useReparadorDeNombres(useMemo(() => [recognition], [recognition]));
  const emisor = nombreDelEmisor(recognition, t, reparar);
  const sinNombre = t('wall.userUnavailable');
  const names = nombresDeReceptores(recognition, reparar, sinNombre);
  // Nombre + al menos un apellido (primeros dos tokens), no solo el primer nombre. El respaldo «Usuario
  // no disponible» NO es un nombre: recortarlo dejaba «Usuario no».
  const shown = names.slice(0, NAMES_SHOWN)
    .map((n) => (n === sinNombre ? n : n.split(' ').slice(0, 2).join(' ')))
    .join(', ');
  const extra = names.length - NAMES_SHOWN;
  const PostItRoot = (profile ? motion.div : 'div') as React.ElementType;

  const draftValid = isEditedMessageValid(draft, minCommentChars);

  const saveEdit = () => {
    if (!draftValid) return;
    update.mutate({ id: recognition.id, body: { mensaje: draft.trim() } }, { onSuccess: () => setEditing(false) });
  };
  const cancelEdit = () => { setEditing(false); setDraft(full); update.reset(); };

  let body: ReactNode;
  if (minimal) {
    body = (
      <div className="postit-minimal">
        <span className="postit-minimal-fund">{fundamentalName ?? t('wall.title')}</span>
      </div>
    );
  } else if (editing) {
    body = (
      <PostItInlineEditor
        draft={draft}
        onDraftChange={setDraft}
        draftValid={draftValid}
        minCommentChars={minCommentChars}
        isPending={update.isPending}
        isError={update.isError}
        error={update.error}
        onCancel={cancelEdit}
        onSave={saveEdit}
      />
    );
  } else {
    body = (
      <>
        <span className="postit-quote-mark">&ldquo;</span>
        <p className={`postit-text ${!done ? 'postit-text--typing' : ''}`}>{typed}</p>
      </>
    );
  }

  return (
    <PostItRoot
      className={`postit ${profile ? 'rp2-postit' : ''} ${profile && (recognition.campanaSecundariaId || recognition.origen !== 'Diario') ? 'rp2-postit--star' : ''}`}
      style={{
        backgroundColor: color,
        '--postit-dark-bg': darkColor,
        transform: `rotate(${PAPER_ROTATIONS[index % PAPER_ROTATIONS.length]}deg)`,
      } as React.CSSProperties}
      onClick={() => openDetailUnlessBusy(editing, justDraggedRef, onOpen)}
      {...(profile ? dragProps(justDraggedRef) : {})}
    >
      <PostItPin recognition={recognition} fundamentalColor={pin} />
      <RecognitionFundIcon recognition={recognition} catalog={catalog} lang={lang} iconClassName="postit-fund-icon" />

      {body}

      <div className={`postit-footer ${done ? 'postit-footer--visible' : ''}`}>
        <span className="postit-from">— {emisor}</span>
        <span className="postit-to">
          {t('wall.for')} {shown}
          {extra > 0 && <span className="postit-more">+{extra}</span>}
        </span>
        <div className="postit-meta">
          <span className="postit-date">{formatDate(recognition.fecha, lang)}</span>
          {/* WP-A5 · RN-P09 — el valor de ESTA nominación, junto a la fecha. Va en `postit-meta`
              porque es el contenedor de metadatos que ya existe: no se inventa zona nueva ni
              lenguaje visual nuevo, que es lo que R6 pide cuando la maqueta no dibuja algo. */}
          <PuntosDeLaNominacion recognition={recognition} />
          {recognition.etiquetaGrupal && (
            <span className="postit-group"><i className="fas fa-users" /> {recognition.etiquetaGrupal}</span>
          )}
          <PostItTagsArea recognition={recognition} />
        </div>
        <PostItReactionBadge recognitionId={recognition.id} reactionsEnabled={recognition.reactionsEnabled ?? true} />
      </div>

      {/* Acciones fuera del footer (demo H-23) — editar es 100% inline; reportar/eliminar abren
          el modal de detalle (mismo flujo ya existente, con confirmación/motivo), para no duplicar
          esa UX en dos lugares distintos con el tiempo disponible en esta fase. */}
      {!editing && (
        <div className="postit-actions-footer" onClick={(e) => e.stopPropagation()}>
          {/* RF-084 — la banderita solo aparece si la geografía de ESTE reconocimiento admite
              reportes (valor efectivo resuelto en servidor). Sin el gate quedaba una afordancia
              muerta: se pulsaba, se abría el modal y ahí ya no había botón Reportar. */}
          {canReportRecognition(recognition) && !isOwner && (
            <button type="button" className="postit-icon-action-btn" title={t('actions.report')} onClick={onOpen}>
              <i className="fas fa-flag" />
            </button>
          )}
          {isOwner && canEdit && (
            <button type="button" className="postit-icon-action-btn" title={t('actions.edit')} onClick={() => { setDraft(full); update.reset(); setEditing(true); }}>
              <i className="fas fa-pen" />
            </button>
          )}
          {isOwner && canDelete && (
            <button type="button" className="postit-icon-action-btn postit-icon-action-btn--danger" title={t('actions.delete')} onClick={onOpen}>
              <i className="fas fa-trash" />
            </button>
          )}
        </div>
      )}
    </PostItRoot>
  );
}

interface WallProps {
  /** Filtros base del feed (sin `page` — la paginación la maneja el muro). */
  baseParams?: Omit<RecognitionFeedParams, 'page'>;
  title?: string;
  emptyText?: string;
  pageSize?: number;
  profilePostIts?: boolean;
  /** REQ-107 — Config de acciones de post-it (toggles de OU/campaña). Sin este prop (caso más
   *  común, muros sin campaña) se resuelve con `resolveDefaultActionConfig` a partir de
   *  `useRecognitionRules().data.allowPostEdit` (Task 15). */
  actionConfig?: PostItActionConfig;
  /** FASE 4 (H-52) — ícono del título del moodboard: ✉️ Enviados / ⭐ Recibidos. Opcional, no
      rompe a los consumidores existentes (PerfilView.tsx) que no lo pasan todavía. */
  titleIcon?: 'sent' | 'received';
  /**
   * FASE 6 (H-71) — modo "canvas del perfil": el contenedor pasa de `.moodboard-grid` a
   * `.rp2-canvas` (grid con fondo y borde punteado, literal del demo) y se oculta la cabecera
   * `.moodboard-header` porque en el perfil del demo el título lo dan las `.rp2-tabs`.
   * Los POST-ITS siguen usando la familia `.postit-*` de F4 (coordinación F4↔F6: no se duplica
   * `.rp2-postit`, ver MAPA-CLASES.md).
   */
  canvas?: boolean;
  /** FASE 6 — nodo a renderizar en lugar del estado vacío por defecto (tarjeta `.rp2-sigma-card`). */
  emptyNode?: ReactNode;
  /**
   * FASE 6 (H-77) — selector de año del perfil. La API (`RecognitionFeedParams`) NO admite filtro
   * por fecha, así que con `year` definido el muro pide un lote grande (`YEAR_FETCH_SIZE`) y filtra
   * + pagina en cliente, igual que hace el demo sobre su dataset completo.
   */
  year?: number;
  /**
   * Paridad demo — rejilla `.moodboard-grid--compact` (gap 8px en vez de 10px). Es lo que usa la
   * home del demo para los dos muros apilados dentro de la misma tarjeta (`HomePage.tsx` : 508).
   */
  compact?: boolean;
  /**
   * Paridad demo — emoji del estado vacío. El demo usa uno distinto por muro: ✨ en Enviados y
   * 🌟 en Recibidos (`HomePage.tsx` : 500 / 534). Sin prop se mantiene el 📝 genérico.
   */
  emptyIcon?: string;
  /**
   * Paridad demo — botón bajo el texto del estado vacío. Solo el muro de Enviados lo tiene
   * (`HomePage.tsx` : 502-504); el de Recibidos no ofrece acción. La clase `.moodboard-empty-cta`
   * ya estaba portada pero era CSS muerta: ningún componente la renderizaba.
   */
  emptyCta?: { label: string; onClick: () => void };
}

/** Tamaño del lote que se pide al filtrar por año en cliente (ver prop `year`). */
const YEAR_FETCH_SIZE = 200;

/** Muro de post-its paginado y conectado al feed real. Reutilizable (home, perfil, campaña). */
export function PostItWall({ baseParams = {}, title, emptyText, pageSize = 6, actionConfig, titleIcon, canvas = false, emptyNode, year, profilePostIts = false, compact = false, emptyIcon, emptyCta }: Readonly<WallProps>) {
  const { t, i18n } = useTranslation('recognition');
  const { demoUser } = useUserContext();
  const [page, setPage] = useState(1);
  const yearMode = typeof year === 'number';
  const feed = useRecognitions({
    ...baseParams,
    page: yearMode ? 1 : page,
    pageSize: yearMode ? YEAR_FETCH_SIZE : pageSize,
  });
  const catalog = useRecognitionCatalog();
  // Reglas EFECTIVAS del programa del muro (`baseParams.programa`): el muro de Brillamos lo pasa
  // 'Brillamos', así `allowPostEdit`/`minCommentChars` salen del override de
  // Brillamos y no de Fundamentales (misma fuga que tenía el botón de carga masiva CSV).
  const rules = useRecognitionRules(undefined, baseParams.programa ?? null);
  const [detail, setDetail] = useState<RecognitionDto | null>(null);
  const cfg = actionConfig ?? resolveDefaultActionConfig(rules.data?.allowPostEdit ?? false);
  // Anti-Spoiler por campaña (2026-09-23) — el servidor QUITA de este muro lo que sigue protegido
  // (salvo lo que uno envió), antes de contar y paginar: aquí no llega ni la tarjeta ni el número. Solo
  // se avisa en el muro de recibidos PROPIO, con las reglas del programa del muro (su campaña activa):
  // en el de un compañero, las reglas son las de quien mira y no dirían nada cierto sobre él.
  const ocultosPorAntiSpoiler = Boolean(rules.data?.antiSpoilerActivoAhora)
    && Boolean(baseParams.receptorId) && baseParams.receptorId === demoUser.userId;
  // Decisión PO 2026-09-23 — el lápiz es POR POST-IT: cada reconocimiento trae `puedeEditar` resuelto
  // por el servidor según SU campaña (ver `puedeEditarTarjeta`). `cfg.canEdit` queda solo como
  // respaldo cuando el servidor no lo calculó. El modal de detalle se evalúa igual, con SU tarjeta.
  // FASE 6 — cambiar de año vuelve a la primera página (igual que el demo).
  useEffect(() => { setPage(1); }, [year]);

  const funds: FundamentalDto[] = catalog.data ?? [];
  const fundById = new Map(funds.map((f) => [f.id, f]));

  const fetched = feed.data?.items ?? [];
  const scopedByYear = yearMode ? fetched.filter((r) => new Date(r.fecha).getFullYear() === year) : fetched;
  const scoped = scopedByYear;
  const total = yearMode ? scoped.length : (feed.data?.totalCount ?? 0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const items = yearMode ? scoped.slice((page - 1) * pageSize, page * pageSize) : scoped;

  const resolvePaper = (r: RecognitionDto, idx: number): { color: string; darkColor: string; pin: string | null } => {
    const first = r.fundamentalIds.map((id) => fundById.get(id)).find(Boolean);
    if (first) {
      const fi = funds.indexOf(first);
      // BUG-01 — el papel se resuelve por el COLOR real del Fundamental (`first.color`), no por su
      // posición en `funds`: el índice deja de ser fiable en cuanto el catálogo llega en un orden
      // distinto al de `FUNDAMENTAL_PALETTE` (ver `getPostItPaperColor` en `postitShared.tsx`).
      const vivid = getFundamentalColor(first, fi);
      // BUG-01 (regresión modo oscuro) — `darkColor` es el mismo tono VIVO del Fundamental (no un
      // pastel): el modo oscuro pintaba antes por posición en el DOM (`body.dark
      // .postit:nth-child(6n+N)` en recognition.css), así que un post-it verde en la posición 1
      // salía naranja. Se resuelve aquí, por Fundamental real, igual que `color` y `pin`.
      return { color: getPostItPaperColor(first.color, fi), darkColor: vivid, pin: vivid };
    }
    return {
      color: POSTIT_PAPER_COLORS[idx % POSTIT_PAPER_COLORS.length],
      darkColor: FUNDAMENTAL_PALETTE[idx % FUNDAMENTAL_PALETTE.length],
      pin: null,
    };
  };

  const icon = titleIcon === 'sent' ? '✉️ ' : titleIcon === 'received' ? '⭐ ' : '';
  const compactClass = compact ? ' moodboard-grid--compact' : '';
  const gridClass = canvas ? 'rp2-canvas' : `moodboard-grid${compactClass}`;

  return (
    <section className="rec-wrap">
      {!canvas && (
        <div className="moodboard-header">
          <h2 className="moodboard-title">{icon}{title ?? t('wall.title')}</h2>
          {total > 0 && <span className="moodboard-count">{t('wall.count', { count: total })}</span>}
        </div>
      )}

      {/* FASE 6 — `isEmpty={false}` a propósito: el estado vacío lo maqueta este componente
          (`.moodboard-empty` / `.rp2-canvas-empty`), no `StateBlock`. Antes se pasaba
          `items.length === 0`, lo que hacía que `StateBlock` cortocircuitara y el bloque
          `.moodboard-empty` que portó F4 (H-52) fuese CÓDIGO MUERTO: nunca llegaba a pintarse.
          Mismo criterio que ya usó F5 en `BrillamosFeed` para su `.bj-empty`. */}
      <StateBlock
        isLoading={feed.isLoading}
        isError={feed.isError}
        error={feed.error}
        isEmpty={false}
        emptyText={emptyText ?? t('wall.empty')}
        onRetry={() => { feed.refetch(); }}
      >
        {ocultosPorAntiSpoiler && (
          <p className="postit-antispoiler-hint" role="status">
            <i className="fas fa-eye-slash" aria-hidden="true" /> {t('wall.antiSpoilerHiddenHint')}
          </p>
        )}
        {items.length === 0 && canvas && (
          <div className="rp2-canvas">
            <div className="rp2-canvas-empty">
              {emptyNode ?? <p className="rp2-empty">{emptyText ?? t('wall.empty')}</p>}
            </div>
          </div>
        )}
        {items.length === 0 && !canvas ? (
          <div className="moodboard-empty">
            <div className="moodboard-empty-content">
              <span className="moodboard-empty-icon">{emptyIcon ?? '📝'}</span>
              <p>{emptyText ?? t('wall.empty')}</p>
              {emptyCta && (
                <button type="button" className="moodboard-empty-cta" onClick={emptyCta.onClick}>
                  {emptyCta.label}
                </button>
              )}
            </div>
          </div>
        ) : null}
        {items.length > 0 && (
          <div className={gridClass}>
            {items.map((r, idx) => {
              const paper = resolvePaper(r, idx);
              const isOwner = Boolean(demoUser.userId && r.emisorId === demoUser.userId);
              return (
                <PostIt
                  key={r.id}
                  recognition={r}
                  index={idx}
                  color={paper.color}
                  darkColor={paper.darkColor}
                  pin={paper.pin}
                  catalog={funds}
                  isOwner={isOwner}
                  canEdit={puedeEditarTarjeta(r, cfg.canEdit)}
                  canDelete={puedeEditarTarjeta(r, cfg.canDelete)}
                  minCommentChars={rules.data?.minCommentChars ?? 0}
                  onOpen={() => setDetail(r)}
                  profile={profilePostIts}
                  minimal={demoUser.tenant === 'Europa'}
                  fundamentalName={r.fundamentalIds.map((id) => fundById.get(id)).find(Boolean)
                    ? getFundamentalName(r.fundamentalIds.map((id) => fundById.get(id)).find(Boolean) as FundamentalDto, i18n.language)
                    : undefined}
                />
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="postit-pagination">
            <button className="btn btn--secondary" type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <i className="fas fa-chevron-left" /> {t('wall.prev')}
            </button>
            <span>{t('wall.pageOf', { page, total: totalPages })}</span>
            <button className="btn btn--secondary" type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              {t('wall.next')} <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </StateBlock>

      {detail && (
        <PostItDetailModal
          recognition={detail}
          catalog={catalog.data}
          onClose={() => setDetail(null)}
          currentUserId={demoUser.userId}
          programa={baseParams.programa ?? null}
          actionConfig={{
            ...cfg,
            canEdit: puedeEditarTarjeta(detail, cfg.canEdit),
            canDelete: puedeEditarTarjeta(detail, cfg.canDelete),
          }}
        />
      )}
    </section>
  );
}
